import { useState } from "react";
import { useProjectTasklist, type ProjectTask } from "@/hooks/useProjectTasklist";
import { useProjectTeam } from "@/hooks/useProjectTeam";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Trash2, CalendarDays, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ProjectTasklistProps {
  projectId: string;
}

const priorityColors: Record<string, string> = {
  urgent: "text-destructive",
  high: "text-orange-500",
  normal: "text-muted-foreground",
  low: "text-muted-foreground/60",
};

export const ProjectTasklist = ({ projectId }: ProjectTasklistProps) => {
  const { activeCompanyId } = useCompanySelector();
  const { tasks, isLoading, addTask, toggleTask, deleteTask } = useProjectTasklist(projectId);
  const { members } = useProjectTeam(projectId);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("normal");
  const [newAssignees, setNewAssignees] = useState<string[]>([]);

  const handleAdd = () => {
    if (!newTitle.trim() || !activeCompanyId) return;
    addTask.mutate({
      title: newTitle.trim(),
      priority: newPriority,
      assigned_to: newAssignees,
      companyId: activeCompanyId,
    });
    setNewTitle("");
    setNewPriority("normal");
    setNewAssignees([]);
  };

  const incompleteTasks = tasks.filter(t => !t.is_complete);
  const completeTasks = tasks.filter(t => t.is_complete);

  return (
    <div className="space-y-3">
      {/* Add task form */}
      <div className="flex items-center gap-2">
        <Input
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
          placeholder="Add a task..."
          className="flex-1"
        />
        <Select value={newPriority} onValueChange={setNewPriority}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={handleAdd} disabled={!newTitle.trim()}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading tasks...</div>}

      {/* Incomplete tasks */}
      <div className="space-y-1">
        {incompleteTasks.map(task => (
          <TaskRow key={task.id} task={task} members={members} onToggle={toggleTask} onDelete={deleteTask} />
        ))}
      </div>

      {/* Completed tasks */}
      {completeTasks.length > 0 && (
        <div className="space-y-1 opacity-60">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-4">Completed</p>
          {completeTasks.map(task => (
            <TaskRow key={task.id} task={task} members={members} onToggle={toggleTask} onDelete={deleteTask} />
          ))}
        </div>
      )}

      {tasks.length === 0 && !isLoading && (
        <p className="text-sm text-muted-foreground text-center py-6">No tasks yet. Add one above.</p>
      )}
    </div>
  );
};

const TaskRow = ({ task, members, onToggle, onDelete }: {
  task: ProjectTask;
  members: any[];
  onToggle: any;
  onDelete: any;
}) => {
  const assignedMembers = members.filter(m => task.assigned_to.includes(m.user_id));

  return (
    <div className={cn(
      "flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors",
      task.is_complete ? "bg-muted/30" : "bg-card hover:bg-accent/30"
    )}>
      <Checkbox
        checked={task.is_complete}
        onCheckedChange={(checked) => onToggle.mutate({ taskId: task.id, isComplete: !!checked })}
      />
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm", task.is_complete && "line-through text-muted-foreground")}>
          {task.title}
        </p>
        {task.due_date && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
            <CalendarDays className="h-3 w-3" />
            {format(new Date(task.due_date), "MMM d")}
          </span>
        )}
      </div>
      {task.priority !== "normal" && (
        <Badge variant="outline" className={cn("text-[10px] px-1.5", priorityColors[task.priority])}>
          {task.priority === "urgent" && <AlertTriangle className="h-3 w-3 mr-0.5" />}
          {task.priority}
        </Badge>
      )}
      <div className="flex -space-x-1.5">
        {assignedMembers.slice(0, 3).map(m => (
          <Avatar key={m.user_id} className="h-6 w-6 border-2 border-background">
            <AvatarImage src={m.profile_photo_url ?? undefined} />
            <AvatarFallback className="text-[9px]">{m.first_name[0]}{m.last_name[0]}</AvatarFallback>
          </Avatar>
        ))}
        {assignedMembers.length > 3 && (
          <span className="text-[10px] text-muted-foreground ml-1">+{assignedMembers.length - 3}</span>
        )}
      </div>
      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => onDelete.mutate(task.id)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};
